/**
 * Core SSO Client Functions
 * Handles authentication flow, token management, and session handling
 */

const DEFAULT_SSO_LOGIN_URL = 'https://sso.kit19.com/login';
const DEFAULT_SSO_LOGOUT_URL = 'https://sso.kit19.com/logout';
const DEFAULT_SSO_API_URL = 'https://auth.kit19.com/api/';

/**
 * Initialize SSO Client with configuration
 */
export class Kit19SSOClient {
  constructor(config = {}) {
    this.config = {
      ssoLoginUrl: config.ssoLoginUrl || DEFAULT_SSO_LOGIN_URL,
      ssoLogoutUrl: config.ssoLogoutUrl || DEFAULT_SSO_LOGOUT_URL,
      ssoApiUrl: config.ssoApiUrl || DEFAULT_SSO_API_URL,
      appName: config.appName || 'Kit19App',
      tokenKey: config.tokenKey || 'kit19_token',
      enableAutoLogin: config.enableAutoLogin !== false,
      onLoginSuccess: config.onLoginSuccess || null,
      onLoginError: config.onLoginError || null,
      onLogout: config.onLogout || null,
      ...config
    };
  }

  /**
   * Redirect to SSO login page with configuration
   */
  login(customConfig = {}) {
    const currentUrl = window.location.origin + window.location.pathname;
    const targetPagePath = window.location.pathname; // Capture the requested page path
    const _origin = window.location.origin;
    const callbackUrl = `${_origin}/callback`; // Always redirect back to /callback

    const ssoConfig = {
      apiBase: this.config.ssoApiUrl,
      loginPage: `${this.config.ssoApiUrl}login.html`,
      tokenKey: this.config.tokenKey,
      redirectOnLogout: customConfig.redirectOnLogout || `${_origin}/login`,
      loginMode: customConfig.loginMode || 'desktopapplogin',
      UserIdGuid: customConfig.UserIdGuid || '',
      isImpersonator: customConfig.isImpersonator || false,
      redirectUrl: customConfig.redirectUrl || callbackUrl,
      targetPagePath: customConfig.targetPagePath || targetPagePath, // Store target page for post-SSO redirect
      ...customConfig
    };

    const url = new URL(this.config.ssoLoginUrl);
    url.searchParams.set('redirect_uri', callbackUrl);
    url.searchParams.set('app', this.config.appName);
    url.searchParams.set('config', encodeURIComponent(JSON.stringify(ssoConfig)));

    window.location.href = url.toString();
  }

  /**
   * Redirect to SSO logout
   */
  logout(returnUrl = null) {
    const logoutUrl = returnUrl
      ? `${this.config.ssoLogoutUrl}?redirect_uri=${encodeURIComponent(returnUrl)}`
      : this.config.ssoLogoutUrl;

    this.clearSession();

    if (this.config.onLogout) {
      this.config.onLogout();
    }

    window.location.href = logoutUrl;
  }

  /**
   * Extract SSO token and config from URL after redirect
   */
  extractTokenFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    const token = params.get('token') || hashParams.get('token');
    const userId = params.get('user_id') || hashParams.get('user_id');
    const isSSOLogin = params.get('sso') || hashParams.get('sso');
    const configParam = params.get('config') || hashParams.get('config');

    let config = null;
    if (configParam) {
      try {
        config = JSON.parse(decodeURIComponent(configParam));
      } catch (e) {
        console.error('Error parsing SSO config:', e);
      }
    }

    return {
      token,
      userId,
      isSSOLogin: isSSOLogin === 'true',
      config
    };
  }

  /**
   * Validate token with auth API
   */
  async validateToken(token) {
    try {
      const response = await fetch(`${this.config.ssoApiUrl}Authorize/ValidateToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ accessToken: token })
      });

      const data = await response.json();

      // Check if outer status is 1 and details.status is 1
      if (data.status === 1 && data.details?.status === 1) {
        return {
          valid: true,
          userData: this.transformUserData(data.details)
        };
      } else {
        return {
          valid: false,
          message: data.message || data.details?.message || 'Token validation failed'
        };
      }
    } catch (error) {
      console.error('Token validation error:', error);
      return {
        valid: false,
        message: error.message || 'Token validation failed'
      };
    }
  }

  /**
   * Transform API response to standardized user data format
   */
  transformUserData(details) {
    const userInfo = details?.userInfo;
    const meta = details?.meta;
    const domainInfo = details?.domainInfo;
    const tokenInfo = details?.tokenInfo;

    return {
      userId: userInfo?.userId,
      User_ID: userInfo?.userId,
      parentId: userInfo?.parentId,
      ParentID: userInfo?.parentId,
      firstName: meta?.userDetail?.fName || meta?.teamChat?.FName || '',
      FName: meta?.userDetail?.fName || meta?.teamChat?.FName || '',
      lastName: meta?.userDetail?.lName || meta?.teamChat?.LName || '',
      LName: meta?.userDetail?.lName || meta?.teamChat?.LName || '',
      email: meta?.userDetail?.email || meta?.teamChat?.EMail || '',
      EMail: meta?.userDetail?.email || meta?.teamChat?.EMail || '',
      mobile: meta?.userDetail?.mobile || meta?.teamChat?.Mobile || '',
      Mobile: meta?.userDetail?.mobile || meta?.teamChat?.Mobile || '',
      profilePicture: meta?.teamChat?.ProfilePicturePath || '',
      ProfilePicturePath: meta?.teamChat?.ProfilePicturePath || '',
      domain: domainInfo?.domainName || '',
      Domain: domainInfo?.domainName || '',
      logo: domainInfo?.companyLogo || '',
      Logo: domainInfo?.companyLogo || '',
      faviconIcon: domainInfo?.faviconIcon || '',
      FaviconIcon: domainInfo?.faviconIcon || '',
      displayName: userInfo?.displayName || '',
      DisplayName: userInfo?.displayName || '',
      username: userInfo?.userLogin || tokenInfo?.username || '',
      LoginName: userInfo?.userLogin || tokenInfo?.username || '',
      companyName: meta?.userDetail?.companyName || domainInfo?.companyName || '',
      CompanyName: meta?.userDetail?.companyName || domainInfo?.companyName || '',
      timezone: meta?.userDetail?.timezone || '',
      countryCode: meta?.userDetail?.countryCode || '',
      tpUserId: userInfo?.tpUserId,
      appName: userInfo?.appName,
      loginMode: userInfo?.loginMode,
      clientIp: userInfo?.clientIp,
      // Domain Info
      domainType: domainInfo?.domainType || '',
      slogan: domainInfo?.slogan || '',
      policyUrl: domainInfo?.policyUrl || '',
      termUrl: domainInfo?.termUrl || '',
      backgroundImage: domainInfo?.backgroundImage || '',
      appLogo: domainInfo?.appLogo || '',
      resellerId: domainInfo?.resellerId || '',
      hyperlinkOnLogo: domainInfo?.hyperlinkOnLogo || ''
    };
  }

  /**
   * Handle SSO callback after redirect
   */
  async handleCallback(onSuccess, onError) {
    const { token, isSSOLogin, config } = this.extractTokenFromUrl();

    if (!isSSOLogin || !token) {
      return false;
    }

    try {
      if (config) {
        this.storeConfig(config);
      }

      const validation = await this.validateToken(token);

      if (validation.valid) {
        this.storeSession(token, validation.userData);
        this.cleanUrlParams();

        if (onSuccess || this.config.onLoginSuccess) {
          (onSuccess || this.config.onLoginSuccess)(validation.userData);
        }

        // Redirect to the originally requested page
        const storedConfig = this.getConfig();
        const targetPage = storedConfig?.targetPagePath || '/';
        window.location.href = targetPage;

        return true;
      } else {
        if (onError || this.config.onLoginError) {
          (onError || this.config.onLoginError)(validation.message);
        }
        return false;
      }
    } catch (error) {
      console.error('SSO callback error:', error);
      if (onError || this.config.onLoginError) {
        (onError || this.config.onLoginError)(error.message);
      }
      return false;
    }
  }

  /**
   * Store session data in localStorage
   */
  storeSession(token, userData) {
    // Store token with multiple keys for compatibility
    localStorage.setItem(this.config.tokenKey, token);
    localStorage.setItem('kit_token', token); // For ProtectedRoute compatibility
    localStorage.setItem('kit_sso_token', token);
    localStorage.setItem('Token', token); // Legacy Kit19Sales key
    localStorage.setItem('kit_userId', userData.userId || userData.User_ID);
    localStorage.setItem('kit_parentId', userData.parentId || userData.ParentID);
    // Legacy Kit19Sales keys
    localStorage.setItem('userId', userData.userId || userData.User_ID);
    localStorage.setItem('ParentID', userData.parentId || userData.ParentID);
    localStorage.setItem('FName', userData.firstName || userData.FName || '');
    localStorage.setItem('LName', userData.lastName || userData.LName || '');
    localStorage.setItem('EMail', userData.email || userData.EMail || '');
    localStorage.setItem('email', userData.email || userData.EMail || ''); // Legacy lowercase key
    localStorage.setItem('Mobile', userData.mobile || userData.Mobile || '');
    localStorage.setItem('ProfilePicturePath', userData.profilePicture || userData.ProfilePicturePath || '');
    localStorage.setItem('kit_domain', userData.domain || userData.Domain || '');
    localStorage.setItem('Logo', userData.logo || userData.Logo || '');
    localStorage.setItem('FaviconIcon', userData.faviconIcon || userData.FaviconIcon || '');
    localStorage.setItem('DisplayName', userData.displayName || userData.DisplayName || '');
    localStorage.setItem('kit_loginName', userData.username || userData.LoginName || '');
    localStorage.setItem('CompanyName', userData.companyName || userData.CompanyName || '');

    // Store domain info
    if (userData.domainType) localStorage.setItem('DomainType', userData.domainType);
    if (userData.slogan) localStorage.setItem('Slogan', userData.slogan);
    if (userData.policyUrl) localStorage.setItem('PolicyUrl', userData.policyUrl);
    if (userData.termUrl) localStorage.setItem('TermUrl', userData.termUrl);
    if (userData.backgroundImage) localStorage.setItem('BackgroundImage', userData.backgroundImage);
    if (userData.appLogo) localStorage.setItem('AppLogo', userData.appLogo);
    if (userData.resellerId) localStorage.setItem('ResellerId', userData.resellerId);
    if (userData.hyperlinkOnLogo) localStorage.setItem('HyperlinkOnLogo', userData.hyperlinkOnLogo);
  }

  /**
   * Store SSO configuration
   */
  storeConfig(config) {
    localStorage.setItem('kit19_sso_config', JSON.stringify(config));
  }

  /**
   * Get stored SSO configuration
   */
  getConfig() {
    const configStr = localStorage.getItem('kit19_sso_config');
    if (!configStr) return null;

    try {
      return JSON.parse(configStr);
    } catch (e) {
      console.error('Error parsing stored config:', e);
      return null;
    }
  }

  /**
   * Clear session data
   */
  clearSession() {
    localStorage.clear();
    sessionStorage.clear();
  }

  /**
   * Check if user has valid session
   */
  isAuthenticated() {
    const token = localStorage.getItem(this.config.tokenKey) || localStorage.getItem('kit_sso_token');
    const userId = localStorage.getItem('kit_userId');

    return !!(token && userId);
  }

  /**
   * Get current user data from localStorage
   */
  getCurrentUser() {
    if (!this.isAuthenticated()) {
      return null;
    }

    return {
      userId: localStorage.getItem('kit_userId'),
      parentId: localStorage.getItem('kit_parentId'),
      firstName: localStorage.getItem('FName'),
      lastName: localStorage.getItem('LName'),
      email: localStorage.getItem('EMail'),
      mobile: localStorage.getItem('Mobile'),
      profilePicture: localStorage.getItem('ProfilePicturePath'),
      domain: localStorage.getItem('kit_domain'),
      logo: localStorage.getItem('Logo'),
      displayName: localStorage.getItem('DisplayName'),
      username: localStorage.getItem('kit_loginName')
    };
  }

  /**
   * Clean SSO parameters from URL
   */
  cleanUrlParams() {
    const url = new URL(window.location.href);

    url.searchParams.delete('token');
    url.searchParams.delete('user_id');
    url.searchParams.delete('sso');
    url.searchParams.delete('config');
    url.hash = '';

    window.history.replaceState({}, document.title, url.pathname + url.search);
  }
}

// Export singleton instance creator
export const createSSOClient = (config) => new Kit19SSOClient(config);

// Export default instance for quick usage
export default Kit19SSOClient;
